
import React, { useEffect, useState, useRef } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { apiRequest } from '../utils/api';
import { Calendar as CalendarIcon, MapPin, Flag, CheckCircle, XCircle, AlertTriangle, RefreshCw, Clock, Lock, Trophy, Zap, Timer, X, ArrowRight, BookOpen, Users, FlaskConical, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { AttendanceStatus, UserRole } from '../types';
import { API_BASE_URL } from '../config';
import * as ReactRouterDOM from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { hasActiveSeason, NoActiveSeason } from '../components/NoActiveSeason';

const { Link } = ReactRouterDOM;

const eventStatusLabel = (status: string) => ({
    COMPLETED: 'Abgeschlossen',
    REGISTRATION_CLOSED: 'Anmeldung geschlossen',
    OPEN_FOR_REGISTRATION: 'Anmeldung offen',
    UPCOMING: 'Bevorstehend',
    CANCELLED: 'Abgesagt',
} as Record<string, string>)[status] || status.replace(/_/g, ' ');

interface CalendarEvent {
    id: string;
    seasonId: string;
    trackId: string;
    roundNumber: number;
    date: string;
    isSprint: boolean;
    isTestRace: boolean;
    distance: string;
    status: string;
    trackName: string;
    trackLocation: string;
    trackFlag?: string;
    userAttendance?: AttendanceStatus | string;
}

const RegistrationTimer: React.FC<{ eventDate: string; onLock: () => void }> = ({ eventDate, onLock }) => {
    const [timeLeft, setTimeLeft] = useState<{h: number, m: number, locked: boolean}>({h:0, m:0, locked: false});

    useEffect(() => {
        const calculateTime = () => {
            const raceTime = new Date(eventDate).getTime();
            const deadline = raceTime - (24 * 60 * 60 * 1000); 
            const now = Date.now();
            const diff = deadline - now;

            if (diff <= 0) {
                setTimeLeft({ h: 0, m: 0, locked: true });
                onLock();
                return;
            }

            const h = Math.floor(diff / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            setTimeLeft({ h, m, locked: false });
        };

        calculateTime();
        const timer = setInterval(calculateTime, 60000); 
        return () => clearInterval(timer);
    }, [eventDate]);

    if (timeLeft.locked) {
        return <Badge color="red" className="flex items-center gap-1"><Lock size={10}/> Anmeldung geschlossen</Badge>;
    }

    return (
        <div className="flex items-center gap-2 text-xs font-mono bg-gray-900 px-2 py-1 rounded border border-gray-700 text-yellow-500">
            <Clock size={12} />
            <span>Schließt in: {timeLeft.h}h {timeLeft.m}m</span>
        </div>
    );
};

export const Calendar: React.FC = () => {
    const { user } = useAuth();
    const { settings, seasons, isLoading: leagueLoading } = useLeague();
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [rulesOk, setRulesOk] = useState(true);
    const [seasonFinished, setSeasonFinished] = useState(false);
    
    // Expanded State instead of InfoEvent Modal
    const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
    const [attList, setAttList] = useState<any[]>([]);
    const [loadingAtt, setLoadingAtt] = useState(false);
    
    // Auto-Scroll Refs
    const eventRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const hasScrolled = useRef(false);
    
    // Track if we have already auto-opened the next race
    const hasAutoExpanded = useRef(false);

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    const loadEvents = async () => {
        try {
            const season = await apiRequest<any>('seasons.php');
            setSeasonFinished(season?.status === 'FINISHED');
            const endpoint = user ? `events.php?userId=${user.id}` : 'events.php';
            const data = await apiRequest<any[]>(endpoint);
            if (Array.isArray(data)) {
                 setEvents(data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        loadEvents();
        if (user && settings.rulebook_updated_at) {
            const rulesDate = new Date(settings.rulebook_updated_at).getTime();
            const userDate = user.rulebook_accepted_at ? new Date(user.rulebook_accepted_at).getTime() : 0;
            const isAdmin = user.roles.includes(UserRole.ADMIN) || user.roles.includes(UserRole.WEBADMIN) || user.roles.includes(UserRole.STEWARD);
            if (!isAdmin && userDate < rulesDate) setRulesOk(false);
            else setRulesOk(true);
        } else setRulesOk(true);
    }, [user, settings]);

    const handleAttendance = async (eventId: string, status: AttendanceStatus) => {
        if (!user) return;
        if (!rulesOk) { alert("Bitte akzeptiere zuerst das aktuelle Regelwerk!"); return; }

        try {
            setEvents(prev => prev.map(e => (e.id === eventId ? { ...e, userAttendance: status } : e)));
            await apiRequest('attendance.php', 'POST', { userId: user.id, eventId, status });
            // If details are open, refresh them to show my change immediately
            if (expandedEventId === eventId) {
                toggleAttendance(eventId, true); 
            }
        } catch (e: any) {
            alert(e.message);
            loadEvents(); 
        }
    };

    const toggleAttendance = async (eventId: string, forceRefresh = false) => {
        if (expandedEventId === eventId && !forceRefresh) {
            setExpandedEventId(null);
            return;
        }

        setExpandedEventId(eventId);
        setLoadingAtt(true);
        try {
            const res = await apiRequest<any[]>(`attendance.php?eventId=${eventId}`);
            setAttList(Array.isArray(res) ? res : []);
        } catch (e) { console.error(e); }
        finally { setLoadingAtt(false); }
    };
    
    // Auto-Expand & Auto-Scroll
    useEffect(() => {
        if (events.length > 0) {
            // Find the next relevant event
            const nextEvent = seasonFinished ? undefined : events.find(e => e.status !== 'COMPLETED');
            
            // Auto Expand if not done yet
            if (nextEvent && !hasAutoExpanded.current) {
                toggleAttendance(nextEvent.id);
                hasAutoExpanded.current = true;
            }

            // Auto Scroll
            if (nextEvent && !hasScrolled.current) {
                 // Small timeout to ensure rendering
                 setTimeout(() => {
                     const el = eventRefs.current[nextEvent.id];
                     if (el) {
                         el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                         hasScrolled.current = true;
                     }
                 }, 300);
            }
        }
    }, [events, seasonFinished]);

    // --- ICS EXPORT ---
    const downloadICS = () => {
        if (events.length === 0) return;

        let icsContent = "BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//SundayDriverCup//LeagueCalendar//DE\nCALSCALE:GREGORIAN\nMETHOD:PUBLISH\n";

        events.forEach(evt => {
            const start = new Date(evt.date);
            const end = new Date(start.getTime() + (90 * 60000)); // Approx 90 mins race
            
            const formatDate = (d: Date) => {
                return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            };

            const title = evt.isTestRace 
                ? `Test Race: ${evt.trackLocation}` 
                : `${settings.league_name} R${evt.roundNumber}: ${evt.trackLocation}`;
                
            const desc = `${evt.trackName}\nDistanz: ${evt.distance}\nFormat: ${evt.isSprint ? 'Sprint' : 'Feature'}`;

            icsContent += "BEGIN:VEVENT\n";
            icsContent += `UID:${evt.id}@${window.location.hostname}\n`;
            icsContent += `DTSTAMP:${formatDate(new Date())}\n`;
            icsContent += `DTSTART:${formatDate(start)}\n`;
            icsContent += `DTEND:${formatDate(end)}\n`;
            icsContent += `SUMMARY:${title}\n`;
            icsContent += `DESCRIPTION:${desc}\n`;
            icsContent += `LOCATION:${evt.trackName}\n`;
            icsContent += "END:VEVENT\n";
        });

        icsContent += "END:VCALENDAR";

        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', 'sdc_season.ics');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Grouping logic for the expanded view
    const groupedAtt = {
        in: attList.filter(a => a.status === 'ATTENDING'),
        out: attList.filter(a => a.status === 'ABSENT'),
        none: attList.filter(a => !a.status || a.status === 'NO_RESPONSE')
    };

    const completedEvents = events.filter(event => event.status === 'COMPLETED').length;
    const openEvents = seasonFinished ? 0 : events.length - completedEvents;

    if (!leagueLoading && !hasActiveSeason(seasons)) {
        return (
            <div className="max-w-7xl mx-auto space-y-6 animate-fade-in pb-10">
                <PageHero
                    eyebrow="Saisonpause"
                    title="Rennkalender"
                    description="Der Rennkalender wird mit der Veröffentlichung der nächsten Saison wieder freigeschaltet."
                    icon={CalendarIcon}
                    accent="#e10600"
                    stats={[{ label: 'Aktive Saison', value: '–' }, { label: 'Offene Rennen', value: 0 }, { label: 'Status', value: 'Pause' }]}
                />
                <NoActiveSeason description="Aktuell gibt es keinen laufenden Rennkalender. Alle vergangenen Rennwochenenden, Ergebnisse und Rennmeldungen findest du vollständig in der Historie." />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-6 animate-fade-in pb-10">
            <PageHero
                eyebrow={seasonFinished ? 'Saisonarchiv' : 'Saisonplan'}
                title="Rennkalender"
                description={seasonFinished
                    ? 'Diese Saison ist abgeschlossen. Kalender, Teilnehmerfelder und Rennergebnisse bleiben im Archiv erhalten.'
                    : 'Der komplette Saisonfahrplan mit Anmeldestatus, Teilnehmerfeld und direktem Weg zu den Rennergebnissen.'}
                icon={CalendarIcon}
                accent="#e10600"
                stats={[
                    { label: 'Rennen', value: events.length },
                    { label: 'Gefahren', value: completedEvents },
                    { label: 'Offen', value: openEvents },
                ]}
                actions={(
                    <div className="flex w-full gap-2 md:w-auto">
                        <Button variant="secondary" icon={Download} onClick={downloadICS} className="flex-1 md:flex-none text-xs">Kalender (.ics)</Button>
                        <Button variant="secondary" icon={RefreshCw} onClick={loadEvents} className="flex-1 md:flex-none text-xs">Aktualisieren</Button>
                    </div>
                )}
            />

            {!rulesOk && (
                <div className="flex items-center justify-between gap-4 rounded-[1.5rem] border border-red-500/30 bg-red-500/10 p-5">
                    <div className="flex items-center gap-3 text-red-200">
                        <AlertTriangle className="shrink-0"/>
                        <span className="text-sm">Anmeldung gesperrt! Bitte akzeptiere das neue Regelwerk.</span>
                    </div>
                    <Link to="/rules">
                        <Button size="sm" variant="danger">Zum Regelwerk</Button>
                    </Link>
                </div>
            )}

            <div className="grid grid-cols-1 gap-5">
                {events.map(event => {
                    const eventDate = new Date(event.date);
                    const isCompleted = event.status === 'COMPLETED';
                    const deadline = new Date(eventDate.getTime() - 24 * 60 * 60 * 1000);
                    // TEST RACE: Never expires for registration (until manually closed or completed)
                    const isDeadlinePassed = !event.isTestRace && new Date() > deadline;
                    const isRegistrationOpen = !seasonFinished && (event.status === 'OPEN_FOR_REGISTRATION' || event.status === 'UPCOMING') && !isDeadlinePassed;
                    
                    const isExpanded = expandedEventId === event.id;

                    return (
                        <div key={event.id} ref={el => { eventRefs.current[event.id] = el; }}>
                        <Card className="group relative overflow-hidden rounded-[2rem] border border-white/[0.075] bg-[#12121a] shadow-[0_26px_70px_rgba(0,0,0,0.25)] transition-all duration-300 hover:-translate-y-0.5 hover:border-white/[0.14]">
                            <div className="absolute inset-x-0 top-0 z-20 h-[3px]" style={{background: `linear-gradient(90deg, ${isCompleted ? '#3b82f6' : '#e10600'}, transparent 70%)`}} />
                            <div className="absolute inset-0 z-0">
                                {event.trackFlag && <img src={getUrl(event.trackFlag)} className="w-full h-full object-cover opacity-30 transform transition-transform duration-700 scale-100 group-hover:scale-105" alt={`${event.trackLocation} Streckenmotiv`}/>} 
                                <div className="absolute inset-0 bg-gradient-to-r from-[#090b10] via-[#11131b]/95 to-[#11131b]/60"></div>
                            </div>

                            <div className="relative z-10 flex flex-col items-center gap-6 p-2 md:flex-row md:p-3">
                                <div className="min-w-[94px] rounded-[1.4rem] border border-white/[0.08] bg-black/35 p-4 text-center shadow-xl backdrop-blur-md">
                                    <div className="text-xs text-gray-400 uppercase font-bold">{eventDate.toLocaleDateString('de-DE', {month: 'short'})}</div>
                                    <div className="text-4xl font-black text-white">{eventDate.getDate()}</div>
                                    <div className="text-xs text-gray-500">{eventDate.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                                </div>

                                <div className="flex-1 text-center md:text-left">
                                    <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                                        {event.isTestRace ? (
                                            <Badge className="bg-blue-900/50 text-blue-300 border-blue-600 flex items-center gap-1"><FlaskConical size={10}/> TEST RACE</Badge>
                                        ) : (
                                            <Badge color="red">Runde {event.roundNumber}</Badge>
                                        )}
                                        <Badge color={event.status==='COMPLETED'?'blue':event.status==='REGISTRATION_CLOSED'?'yellow':'green' as any}>{eventStatusLabel(event.status)}</Badge>
                                    </div>
                                    <h2 className="mb-2 text-3xl font-black uppercase italic leading-none tracking-tight text-white md:text-4xl">{event.trackLocation}</h2>
                                    <p className="text-gray-400 font-bold text-sm flex items-center justify-center md:justify-start gap-1">
                                        <MapPin size={14} /> {event.trackName}
                                    </p>
                                </div>

                                <div className="min-w-[200px] flex flex-col items-center md:items-end gap-3 w-full md:w-auto">
                                    <div className="flex flex-col md:flex-row gap-2 w-full">
                                        <button 
                                            onClick={() => toggleAttendance(event.id)} 
                                            className={`flex flex-1 items-center justify-center gap-2 rounded-[1rem] border px-4 py-2.5 text-xs font-bold uppercase transition-all md:flex-none ${isExpanded ? 'border-white/15 bg-white/10 text-white' : 'border-white/[0.08] bg-black/30 text-gray-400 hover:border-white/15 hover:text-white'}`}
                                        >
                                            <Users size={16}/> 
                                            Teilnehmer
                                            {isExpanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                                        </button>
                                        
                                        {isCompleted ? (
                                            <Link to={`/event/${event.id}`} className="flex-1 md:flex-none">
                                                <Button icon={Trophy} size="sm" className="w-full">Ergebnisse</Button>
                                            </Link>
                                        ) : null}
                                    </div>
                                    
                                    {!isCompleted && !seasonFinished && (
                                        <div className="w-full flex flex-col items-center md:items-end gap-2">
                                            {!event.isTestRace && !isDeadlinePassed && <RegistrationTimer eventDate={event.date} onLock={() => loadEvents()} />}
                                            
                                            {/* For Test Races: Always show registration if not completed/closed */}
                                            {user && (isRegistrationOpen || (event.isTestRace && event.status !== 'REGISTRATION_CLOSED')) && rulesOk && (
                                                <div className="flex w-full rounded-[1rem] border border-white/[0.08] bg-black/35 p-1 shadow-xl md:w-auto">
                                                    <button onClick={() => handleAttendance(event.id, AttendanceStatus.ATTENDING)} className={`flex-1 md:flex-none px-6 py-2 text-sm font-bold uppercase rounded flex items-center justify-center gap-2 transition-all ${event.userAttendance === AttendanceStatus.ATTENDING ? 'bg-green-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                                                        <CheckCircle size={16}/> DABEI
                                                    </button>
                                                    <button onClick={() => handleAttendance(event.id, AttendanceStatus.ABSENT)} className={`flex-1 md:flex-none px-6 py-2 text-sm font-bold uppercase rounded flex items-center justify-center gap-2 transition-all ${event.userAttendance === AttendanceStatus.ABSENT ? 'bg-red-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                                                        <XCircle size={16}/> FEHLE
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* EXPANDABLE ATTENDANCE LIST - INSIDE CARD */}
                            {isExpanded && (
                                <div className="relative z-10 -mx-6 -mb-6 mt-5 animate-fade-in border-t border-white/[0.06] bg-black/35 px-6 pb-7 pt-6">
                                    {loadingAtt ? (
                                        <div className="p-8 text-center text-gray-400 flex items-center justify-center gap-2">
                                            <RefreshCw className="animate-spin" size={16}/> Lade Teilnehmer...
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            {/* ZUGESAGT */}
                                            <div className="rounded-[1.35rem] border border-green-500/20 bg-green-500/[0.07] p-4">
                                                <div className="flex justify-between items-center mb-3 pb-2 border-b border-green-900/30">
                                                    <span className="text-green-500 font-bold uppercase text-xs flex items-center gap-2">
                                                        <CheckCircle size={14}/> Zugesagt
                                                    </span>
                                                    <Badge color="green">{groupedAtt.in.length}</Badge>
                                                </div>
                                                <div className="space-y-1 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                                                    {groupedAtt.in.map((a,i) => (
                                                        <div key={i} className="flex items-center gap-2 text-sm text-gray-300 bg-black/20 p-1.5 rounded">
                                                            <div className="w-1 h-4 rounded-full" style={{backgroundColor: a.team_color}}></div>
                                                            {a.username}
                                                        </div>
                                                    ))}
                                                    {groupedAtt.in.length === 0 && <div className="text-gray-600 italic text-xs">Keine Zusagen.</div>}
                                                </div>
                                            </div>

                                            {/* ABGEMELDET */}
                                            <div className="rounded-[1.35rem] border border-red-500/20 bg-red-500/[0.07] p-4">
                                                <div className="flex justify-between items-center mb-3 pb-2 border-b border-red-900/30">
                                                    <span className="text-red-500 font-bold uppercase text-xs flex items-center gap-2">
                                                        <XCircle size={14}/> Abgemeldet
                                                    </span>
                                                    <Badge color="red">{groupedAtt.out.length}</Badge>
                                                </div>
                                                <div className="space-y-1 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                                                    {groupedAtt.out.map((a,i) => (
                                                        <div key={i} className="flex items-center gap-2 text-sm text-gray-400 bg-black/20 p-1.5 rounded">
                                                            <div className="w-1 h-4 rounded-full" style={{backgroundColor: a.team_color || '#4b5563'}}></div>
                                                            {a.username}
                                                        </div>
                                                    ))}
                                                    {groupedAtt.out.length === 0 && <div className="text-gray-600 italic text-xs">Niemand abgemeldet.</div>}
                                                </div>
                                            </div>

                                            {/* OFFEN */}
                                            <div className="rounded-[1.35rem] border border-white/[0.08] bg-white/[0.035] p-4">
                                                <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-700">
                                                    <span className="text-gray-400 font-bold uppercase text-xs flex items-center gap-2">
                                                        <AlertTriangle size={14}/> Ausstehend
                                                    </span>
                                                    <Badge color="gray">{groupedAtt.none.length}</Badge>
                                                </div>
                                                <div className="space-y-1 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                                                    {groupedAtt.none.map((a,i) => (
                                                        <div key={i} className="text-sm text-gray-500 p-1.5 truncate">
                                                            {a.username}
                                                        </div>
                                                    ))}
                                                    {groupedAtt.none.length === 0 && <div className="text-gray-600 italic text-xs">Alle haben reagiert.</div>}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
